import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ButtonWrapper from "../../components/ButtonWrapper";
import { useDispatch, useSelector } from "react-redux";
import { transactionApi } from "../../config/axiosConfig";
import { Select, Spin, Alert } from "antd";
import { postMyrCreateTransaction } from "../../redux/actions/postQrInitManual";
import { useNavigate, useParams } from "react-router-dom";
import { VNDBankShortnames } from "../../helpingFunctions";
import ErrorGenerating from '../../assets/error-generating.png'

const { Option } = Select;

const CardWrapper = styled.div`
  height: auto;
  margin-bottom: 20px;
  @media (min-width: 280px) and (max-width: 768px) {
    width: 90%;
  }
  width: 30%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border-radius: 10px;

  .card-header {
    background: #162056;
    padding: 20px;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  h3 {
    color: white;
  }
`;

const StyledWrapper = styled(Select)`
  width: 100%;

  &.ant-select:not(.ant-select-customize-input) .ant-select-selector {
    width: 100%;
    border-radius: 10px;
  }
`;

const VNDTransactionInfoCard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const [bankMenu, setBankMenu] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [selectedBank, setSelectedBank] = useState("");
  const [loadingNext, setLoadingNext] = useState(false);

  const verifyManualTransactoinState = useSelector(
    (state) => state.getVerifyManualTransactionReducer
  );
  const { data } = verifyManualTransactoinState;

  const submitTransaction = async () => {
    setLoadingNext(true);

    // No bankId: EfgPay's /pay takes no bank parameter and returns the receiving bank itself.
    const payload = {
      transactionId: data.trxRef,
      type: "vietqr",
      amount: data.amount,
      sessionId: data.sessionToken,
      userId: data.merchantId,
    };

    try {
      await dispatch(
        postMyrCreateTransaction(payload, navigate, params?.id, "vnd", data?.trafficShift)
      );
    } finally {
      setLoadingNext(false);
    }
  };

  useEffect(() => {
    // EfgPay path: no bank is chosen here at all. EfgPay's /pay takes no bank parameter and
    // returns the receiving bank, sub bank and QR in its own response, so the page used to fetch
    // a list only to pick from it at random. That list came from the KPay endpoint, and when KPay
    // stopped answering, the page blanked with "No banks available" on a rail KPay has no part in.
    const submitWithoutBank = () => {
      setLoadingBanks(false);
      setTimeout(() => {
        submitTransaction();
      }, 300);
    };

    const fetchBanks = async () => {
      try {
        const res = await transactionApi.get(
          "vnd/transactionvnd/vnd-one1-bank-list"
        );
        let banks = [];

        // ONE1 response
        if (res.data?.data?.data && Array.isArray(res.data.data.data)) {
          banks = res.data.data.data.map((bank) => ({
            bankCode: bank.id,
            bankName: bank.bank_name,
            short_name: VNDBankShortnames[bank.id] || "",
          }));
        }

        setBankMenu(banks);
      } catch (err) {
        console.error("Error fetching banks", err);
      } finally {
        setLoadingBanks(false);
      }
    };

    if (data?.trxRef) {
      if (data?.trafficShift) {
        fetchBanks();
      } else {
        submitWithoutBank();
      }
    }
  }, [data]);

  const handleNext = async () => {
    if (!selectedBank) return;

    setLoadingNext(true);

    const getBank = bankMenu.find(
      (item) => item.bankName === selectedBank
    );

    const payload = {
      transactionId: data.trxRef,
      type: "vietqr",
      amount: data.amount,
      sessionId: data.sessionToken,
      userId: data.merchantId,
      bankId: getBank?.bankCode,
      bankName: getBank?.bankName
    };

    try {
      await dispatch(
        postMyrCreateTransaction(payload, navigate, params?.id, "vnd", data?.trafficShift)
      );
    } finally {
      setLoadingNext(false);
    }
  };

  return (
    <CardWrapper>
      <div className="card-header w-100">
        <h3 className="text-center">Transaction Info</h3>
      </div>
  
      <div className="w-100">  
        {/* 🚨 EMPTY BANK LIST — only the OnePay path has a list to be empty */}
        {data?.trafficShift && !loadingBanks && bankMenu.length === 0 ? (
          <div className="w-100 text-center">
            <img
              src={ErrorGenerating}
              alt="No banks available"
              style={{ width: '50%', marginBottom: 20 }}
            />
            <Alert
              type="warning"
              message="No banks available at the moment. Please try again later."
              showIcon
            />
          </div>
        ) : data?.trafficShift ? (
          /* 🏦 BANK SELECTION FOR ONEPAY */
          /* 👇 NORMAL MANUAL FLOW (UNCHANGED) */
          <div style={{padding: 20}}>
              <p style={{ marginBottom: 12, marginTop: 10 }}>
                Amount: 
                <b> ₫
                  {new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(data?.amount ?? 0)}{" "}
                </b>
              </p>
              <p className="label mb-5">
                Reference: <b>{data?.trxRef}</b>
              </p>
              
            <p className="label mb-0">Select Bank*</p>
            <StyledWrapper
              className="w-100 rounded"
              size="large"
              value={selectedBank}
              onChange={(value) => setSelectedBank(value)}
            >
              <Option value="">
                {loadingBanks ? "Loading banks..." : "Select Bank"}
              </Option>
  
              {bankMenu.map((item) => {
                const label = item.short_name
                  ? `${item.short_name} - ${item.bankName}`
                  : item.bankName;
  
                return (
                  <Option
                    title={label}
                    key={item.bankCode}
                    value={item.bankName}
                  >
                    {label}
                  </Option>
                );
              })}
            </StyledWrapper>
  
            <div className="w-100 mt-3">
              <ButtonWrapper
                border
                size="middle"
                height="40px"
                className="w-100 mt-4"
                text={loadingNext ? "Loading..." : "Next"}
                onClick={handleNext}
                disabled={loadingNext}
              />
            </div>
          </div>
        ) : (
          /* 🔄 AUTO FLOW LOADING FOR EFGPAY */
          <div
            className="w-100 d-flex flex-column align-items-center"
            style={{ padding: "30px 0" }}
          >
            <Spin size="large" />
            <p className="mt-3 mb-0" style={{ fontWeight: 500 }}>
              Verifying...
            </p>
          </div>
        )}
      </div>
    </CardWrapper>
  );
  
};

export default VNDTransactionInfoCard;
